import rightArrow from "@assets/icons/rightArrow.svg";
import { RecentArticlesSection } from "@components/Blocks/RecentArticlesSection";
import { TopTablaNewsSection } from "@components/Blocks/TopTablaNewsSection";
import { ArticleGridCards } from "@components/Cards/Layout/ArticleGridCards";
import MainContainer from "@components/Container/MainContainer";
import { BASE_URL } from "@config/SitemapConstants";
import { AdsLb2, AdsLb3 } from "@elements/Advertisement/variant/AdsLb";
import Heading from "@elements/Typography/Heading";
import { getGTMData } from "@helper/getMetaData";
import { colorConstants, DEFAULT_COLOR } from "@helper/sectionConstants";
import { Route } from "@route-types/Home/+types/Home";
import ErrorPage from "@routes/StatusHandler/StatusHandler";
import {
  fetchContext,
  ResponseType,
  TRouteWithoutRedirect,
  TRouteWithRedirect,
} from "@sphtech/web2-core/ssr";
import {
  createMetaTags,
  type MetaTagsProps,
  USFWebSiteSchema,
} from "@sphtech/web2-seo-schema";
import { LOCALHOST } from "@src/app/config/MetaTagsConstants";
import { PageAdTargetingTypeEnum } from "@typings/Ads.d";
import type { GADataProps } from "@typings/PageMetaData";
import {
  HomePageDataPayload,
  HomeSectionArticlesPayload,
} from "@typings/PagePayloadType";
import React from "react";
import { ClientLoaderFunctionArgs, Link, useLoaderData } from "react-router";
import { twMerge } from "tailwind-merge";

import fetchHomeData from "./Home.server";

export function meta(args: Route.MetaArgs) {
  const dataLoaderResponse = args.loaderData as TRouteWithRedirect<
    HomePageDataPayload,
    string
  >;
  if (dataLoaderResponse.type === ResponseType.SUCCESS) {
    const metaData = dataLoaderResponse.payload.metaTags as MetaTagsProps;
    return createMetaTags(metaData);
  }
}

type SectionOrderItem = {
  key: keyof HomeSectionArticlesPayload;
  size: number;
};

const sectionOrder: SectionOrderItem[] = [
  { key: "communityArticles", size: 5 },
  { key: "cultureArticles", size: 5 },
  { key: "newsArticles", size: 5 },
  { key: "lifestyleArticles", size: 5 },
];

export default function HomePage() {
  const routeContext: TRouteWithoutRedirect<HomePageDataPayload, string> =
    useLoaderData();

  const dataLoaderResponse = routeContext;
  if (dataLoaderResponse.type) {
    return <ErrorPage response={dataLoaderResponse} />;
  }

  const topTablaArticles = dataLoaderResponse.payload.topTablaArticles;
  const sectionArticles = dataLoaderResponse.payload.sectionArticles;
  const epaperCoverImageUrl =
    dataLoaderResponse.payload.epaperCoverImage.coverImageUri;

  const section = "home";
  const pageAdTargetValue = PageAdTargetingTypeEnum.HOME;

  return (
    <>
      <USFWebSiteSchema
        name="Tabla"
        url={BASE_URL}
        searchUrl="https://www.tabla.com.sg/search/site/{search_term_string}"
      />
      <MainContainer
        pageAdTargetValue={pageAdTargetValue}
        showTmBanner={true}
        epaperCoverImageUrl={epaperCoverImageUrl}
        sectionName={section}
        bigHeader={true}
      >
        {topTablaArticles.length >= 4 && (
          <TopTablaNewsSection
            topNews={topTablaArticles}
            epaperCoverImageUrl={epaperCoverImageUrl}
          />
        )}

        <AdsLb2
          sectionName={section}
          pageAdTargetType={pageAdTargetValue}
          rootClassName={
            "py-xxm pt-2.5 pb-5 md:px-2 bg-grey-950 md:py-xxs lg:pt-4 lg:pb-6 lg:px-0"
          }
          index={0}
        />

        {sectionOrder.map((section, index) => {
          const articles = sectionArticles[section.key].slice(0, section.size);
          const sectionName = sectionArticles[section.key][0]?.sectionName;
          const colorConstantsKey = sectionName as keyof typeof colorConstants;
          if (!sectionName) {
            return null;
          }
          const sectionPath = sectionName.toLowerCase();
          const SectionComponent = (
            <div className="block">
              {index !== 0 && index % 2 === 0 && (
                <>
                  <AdsLb3
                    sectionName={"home"}
                    pageAdTargetType={pageAdTargetValue}
                    rootClassName={
                      "relative w-full py-xxm pt-2.5 bg-grey-950 pb-5 md:px-2 md:py-xxs lg:pt-4 lg:pb-6 lg:px-0"
                    }
                    index={0}
                  />
                </>
              )}

              <div
                className={twMerge(
                  "px-3 xl:px-0 md:max-w-screen-xmd xmd:max-w-screen-lg lg:max-w-7xl bg-white m-auto gap-2 md:gap-3 flex flex-col",
                  index % 2 === 0 ? "py-5 lg:py-10" : "py-5 xmd:py-8",
                )}
              >
                <Heading
                  Element="h2"
                  className="text-grey-200 text-40 lg:text-100 font-bold text-center uppercase font-primary w-max mx-auto border-t-[3px] border-solid leading-10 px-1 py-2"
                  style={{
                    borderColor: sectionName
                      ? colorConstants[colorConstantsKey].color.lightBg
                      : DEFAULT_COLOR,
                  }}
                >
                  <Link
                    reloadDocument
                    to={`/${sectionPath}?ref=home-${sectionPath}`}
                    rel="noreferrer"
                  >
                    {sectionName}
                  </Link>
                </Heading>

                <div className="inline-grid xmd:block xmd:p-0">
                  {index % 2 !== 0 ? (
                    <>
                      {/* Mobile: stacked list */}
                      <div className="xmd:hidden">
                        <RecentArticlesSection
                          recentArticles={articles}
                          sectionName={sectionName}
                          enableSubmenus={false}
                          rootClassName="!mt-0"
                          refMedium={`home-${sectionPath}`}
                        />
                      </div>
                      {/* Desktop: 4-column grid */}
                      <div className="hidden xmd:block">
                        <ArticleGridCards
                          article={articles.slice(0, 4)}
                          articleIndex={index}
                          rootClassName={
                            "inline-flex xmd:flex pb-2 md:inline-grid md:grid-cols-4 xmd:inline-grid lg:grid lg:max-w-7xl bg-white m-auto"
                          }
                          cardWrapper={
                            "flex-col flex-shrink-0 md:relative md:flex-col xmd:max-w-[194px] xl:min-w-[306px] md:max-w-[210px] xmd:max-w-full w-full xmd:py-0"
                          }
                          imageWrapperStyle={
                            "max-w-[194px] xl:min-w-[306px] w-full"
                          }
                          refMedium={`home-${sectionPath}`}
                        />
                      </div>
                    </>
                  ) : (
                    <RecentArticlesSection
                      recentArticles={articles}
                      sectionName={sectionName}
                      enableSubmenus={false}
                      rootClassName="!mt-0"
                      refMedium={`home-${sectionPath}`}
                    />
                  )}
                  {Array.isArray(articles) && (
                    <div
                      className={twMerge(
                        "grid justify-items-end leading-5 font-bold text-sm font-opensans mt-3 pr-3 md:pr-0",
                      )}
                    >
                      <Link
                        reloadDocument
                        to={`/${sectionPath}?ref=home-${sectionPath}`}
                        rel="noreferrer"
                        className="flex gap-1"
                      >
                        View more
                        <img
                          src={rightArrow}
                          alt={"right-arrow"}
                          loading="lazy"
                          width={16}
                          height={16}
                        />
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );

          return (
            <React.Fragment key={section.key}>
              {SectionComponent}
            </React.Fragment>
          );
        })}

        {/* <div className="block bg-grey-600 bg-opacity-10 p-6">
          <div className="block px-3 xl:px-0 md:max-w-screen-xmd xmd:max-w-screen-lg lg:max-w-7xl  m-auto">
            <div className="inline-grid xmd:block xmd:p-0 ">
              Placement for Taboola widget on Home page. Please remove this comment when Taboola is enabled on Home page.
            </div>
          </div>
        </div> */}
      </MainContainer>
    </>
  );
}

export async function loader({ request }: Route.LoaderArgs) {
  const data = await fetchHomeData();

  const url = new URL(request.url, LOCALHOST);
  const gtmData = getGTMData({
    title: "home_index",
    level2: "home",
    url: url.toString(),
  } as GADataProps);
  // Append GTM data for SSR
  return {
    ...data,
    gtm: gtmData,
  };
}

export async function clientLoader(args: ClientLoaderFunctionArgs) {
  const result = (await fetchContext(args)) as { context?: unknown };

  if (!result.context) {
    console.error(
      "Error occured during client-side routing, returning server Loader Data",
    );
    return await args.serverLoader();
  }

  const typedResult: unknown = result.context;

  // Check for valid payload field
  if (
    !typedResult ||
    typeof typedResult !== "object" ||
    !("payload" in typedResult) ||
    !Array.isArray(typedResult.payload)
  ) {
    console.error("Client Loader: fetchContext() returned an invalid payload");
  }
  return typedResult as Promise<
    TRouteWithRedirect<HomePageDataPayload, string>
  >;
}
